import React, { KeyboardEvent, useState } from 'react'

import { Popover } from '@mui/material'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

import SearchField from 'components_new/molecules/SearchField'

// Class the header cell targets to reveal the trigger on hover/focus.
export const COLUMN_SEARCH_TRIGGER_CLASS = 'column-search-trigger'

// sx selector for the header cell: reveal the trigger while the cell is
// hovered or holds keyboard focus.
export const COLUMN_SEARCH_REVEAL_SELECTOR = `&:hover .${COLUMN_SEARCH_TRIGGER_CLASS}, &:focus-within .${COLUMN_SEARCH_TRIGGER_CLASS}`

const TRIGGER_SIZE = 24

// The sort arrow's size, so the two header glyphs match. Relative to the
// header's own font size, the way the sort label sets it.
export const SORT_ARROW_FONT_SIZE = '1.25em'

/**
 * Room the header always reserves for the trigger, so revealing it on hover
 * never moves the column label: the button plus 20px clearance for the sort
 * arrow, which floats out of flow beside the label.
 */
export const COLUMN_SEARCH_SLOT_WIDTH = TRIGGER_SIZE + 20

/**
 * The header row spaces its label and this slot apart, so the slot ends up at
 * the far edge of the column, inside the cell's own padding. The fixed width
 * is what a column with nothing to spare falls back to: the trigger then sits
 * a clearance away from the label rather than on top of it.
 */
const slotSx = {
  display: 'flex',
  flexShrink: 0,
  width: COLUMN_SEARCH_SLOT_WIDTH
}

// Balances the trigger slot on the opposite side, so a centered column label
// stays centered.
export const ColumnSearchSpacer = () => (
  <Box aria-hidden={true} sx={slotSx} />
)

export interface ColumnSearchProps {
  label: string
  onChange: (query: string) => void
  onClose: () => void
  onOpen: () => void
  open: boolean
  // Which edge of the header cell the button sits against: the outer one,
  // away from the label. Right aligned columns label from the right, so
  // their outer edge is the left one.
  placement: 'start' | 'end'
  query: string
}

/**
 * A column's free-text search: a magnifying glass in the header that stays
 * invisible until the header is hovered or focused, and opens a popover with
 * the search field. A column with an active query keeps its trigger visible
 * in brand colour, with the query in the tooltip.
 */
const ColumnSearch = (props: ColumnSearchProps) => {
  const { label, onChange, onClose, onOpen, open, placement, query } = props

  const { t } = useTranslation()

  // Callback ref rather than state in the popover: the anchor survives the
  // header re-rendering on every keystroke.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const active = query.length > 0

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      onClose()
    }
  }

  return (
    <Box
      className={active ? undefined : COLUMN_SEARCH_TRIGGER_CLASS}
      ref={setAnchorEl}
      sx={{
        ...slotSx,
        // The button sits on the outer edge of the slot, away from the label.
        justifyContent: placement === 'start' ? 'flex-start' : 'flex-end',
        ...(active || open
          ? null
          : { opacity: 0, transition: 'opacity 120ms ease-in-out' })
      }}
    >
      <Tooltip
        title={
          open
            ? ''
            : active
              ? `${label}: ${query}`
              : t('global', 'search_in_column')
        }
      >
        <IconButton
          aria-label={t('global', 'search_in_column')}
          color={active ? 'brand' : 'default'}
          onClick={() => (open ? onClose() : onOpen())}
          size="small"
          sx={{
            // Inherit the header's font size, so the glyph below sizes off
            // the same base as the sort arrow.
            fontSize: 'inherit',
            height: TRIGGER_SIZE,
            p: 0.25,
            width: TRIGGER_SIZE
          }}
        >
          <Icon
            fontSize="inherit"
            name="Search"
            sx={{ fontSize: SORT_ARROW_FONT_SIZE }}
          />
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        disableScrollLock={true}
        onClose={onClose}
        open={open && Boolean(anchorEl)}
        slotProps={{
          paper: {
            onClick: (event: React.MouseEvent) => event.stopPropagation(),
            onMouseDown: (event: React.MouseEvent) => event.stopPropagation(),
            sx: { p: 1, width: 280 }
          }
        }}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
      >
        <Box onKeyDown={handleKeyDown}>
          <SearchField
            autoFocus={true}
            onClear={() => onChange('')}
            onSearch={onChange}
            placeholder={t('global', 'search_in_column_placeholder', false, {
              column: label
            })}
            size="small"
            value={query}
          />
        </Box>
      </Popover>
    </Box>
  )
}

export default ColumnSearch
